import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  Typography
} from "antd";
import { DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import {
  createVehicle,
  deleteVehicle,
  emailPlans,
  planPdfUrl,
  saveSettings,
  updateStaff,
  updateVehicle
} from "./api";
import { startDrag } from "./Board";
import { addDays, daysBetween, dayOf } from "./calc";
import { dayLabel, kindLabels, qualificationLabels, timeRange } from "./labels";

const formatDay = day => (day ? day.split("-").reverse().join(".") : "");

/**
 * What still needs planning: calendar appointments without anyone, maintenance visits with only
 * a proposed time, dated tasks without a team. Entries are dragged onto the board (desktop) or
 * planned with the button.
 */
export const QueueDrawer = ({ open, queue, canPlan, onClose, onPlan }) => {
  const { t, i18n } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const kinds = kindLabels(t);
  return (
    <Drawer
      rootClassName="dispatch-page"
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 400}
      title={t("To be planned")}
      mask={isMobile}
    >
      {!queue?.length ? (
        <Empty description={t("Everything is planned")} />
      ) : (
        <List
          dataSource={queue}
          renderItem={entry => (
            <List.Item
              className="dsp-queue__item"
              draggable={canPlan && !isMobile}
              onDragStart={event => startDrag(event, { type: "queue", entry })}
              actions={
                canPlan
                  ? [
                      <Button key="plan" size="small" type="primary" onClick={() => onPlan(entry)}>
                        {t("Plan")}
                      </Button>
                    ]
                  : []
              }
            >
              <List.Item.Meta
                title={
                  <Space wrap size={4}>
                    <Tag>{kinds[entry.kind]}</Tag>
                    <span className="dsp-wrap">{entry.title}</span>
                  </Space>
                }
                description={
                  <>
                    <div>
                      {entry.start
                        ? `${dayLabel(dayOf(entry.start), i18n.language)} ${timeRange(entry)}`
                        : entry.day
                          ? formatDay(entry.day)
                          : ""}
                      {entry.dueDate &&
                        ` · ${t("due {{date}}", { date: formatDay(entry.dueDate) })}`}
                    </div>
                    {entry.client && <div>{entry.client.name}</div>}
                    {entry.address && <div className="dsp-small">{entry.address}</div>}
                    {entry.proposals?.length > 0 && (
                      <div className="dsp-small">
                        {t("Proposed to the customer: {{count}} slots", {
                          count: entry.proposals.length
                        })}
                      </div>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

const VehicleForm = ({ open, vehicle, licenseClasses, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      vehicle
        ? {
            ...vehicle,
            nextInspection: vehicle.nextInspection ? dayjs(vehicle.nextInspection) : null,
            nextService: vehicle.nextService ? dayjs(vehicle.nextService) : null
          }
        : { seats: 2, licenseClass: "B" }
    );
  }, [open, vehicle]);
  const save = async () => {
    const v = await form.validateFields();
    const body = {
      ...v,
      nextInspection: v.nextInspection ? v.nextInspection.format("YYYY-MM-DD") : "",
      nextService: v.nextService ? v.nextService.format("YYYY-MM-DD") : ""
    };
    setSaving(true);
    try {
      if (vehicle) await updateVehicle(vehicle.id, body);
      else await createVehicle(body);
      Success(t("Saved"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      rootClassName="dispatch-page"
      open={open}
      onCancel={onClose}
      title={vehicle ? t("Edit vehicle") : t("New vehicle")}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>
          {t("Save")}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label={t("Name")}
              rules={[{ required: true, message: t("Please enter a name") }]}
            >
              <Input maxLength={120} placeholder="Sprinter" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="plate" label={t("Licence plate")}>
              <Input maxLength={20} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="seats" label={t("Seats")}>
              <InputNumber min={1} max={60} precision={0} className="dsp-full" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="licenseClass" label={t("Licence class needed")}>
              <Select
                allowClear
                options={(licenseClasses || []).map(c => ({ value: c, label: c }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="nextInspection" label={t("Next inspection (TÜV)")}>
              <DatePicker className="dsp-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="nextService" label={t("Next service")}>
              <DatePicker className="dsp-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t("Notes")}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const inspectionTag = (vehicle, t) => {
  if (!vehicle.nextInspection) return null;
  const left = daysBetween(dayOf(new Date()), vehicle.nextInspection);
  const color = left < 0 ? "error" : left <= 30 ? "warning" : "default";
  return <Tag color={color}>{t("TÜV {{date}}", { date: formatDay(vehicle.nextInspection) })}</Tag>;
};

/** Vehicles and the employees' skills / visibility on the board. */
export const ResourcesDrawer = ({ open, meta, onClose, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(undefined);
  const canEdit = meta?.can?.update;
  const vehicles = meta?.vehicles || [];

  const removeVehicle = async vehicle => {
    try {
      const res = await deleteVehicle(vehicle.id);
      Success(
        res.data.data.archived ? t("Archived: the vehicle is used on planned jobs") : t("Removed")
      );
      onChanged();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const saveStaff = async (person, body) => {
    try {
      await updateStaff(person.id, body);
      onChanged();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <Drawer
      rootClassName="dispatch-page"
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 560}
      title={t("Vehicles and team")}
    >
      <Tabs
        items={[
          {
            key: "vehicles",
            label: t("Vehicles"),
            children: (
              <>
                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={() => setEditing(null)} block>
                    {t("New vehicle")}
                  </Button>
                )}
                <List
                  dataSource={vehicles}
                  locale={{ emptyText: t("No vehicles yet") }}
                  renderItem={v => (
                    <List.Item
                      actions={
                        canEdit
                          ? [
                              <Button
                                key="edit"
                                type="text"
                                icon={<EditOutlined />}
                                aria-label={t("Edit")}
                                onClick={() => setEditing(v)}
                              />,
                              v.active && (
                                <Popconfirm
                                  key="delete"
                                  title={t("Remove this vehicle?")}
                                  okText={t("Remove")}
                                  cancelText={t("Cancel")}
                                  onConfirm={() => removeVehicle(v)}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    aria-label={t("Remove")}
                                  />
                                </Popconfirm>
                              )
                            ].filter(Boolean)
                          : []
                      }
                    >
                      <List.Item.Meta
                        title={
                          <Space wrap size={4}>
                            <span>{v.name}</span>
                            {v.plate && <Tag>{v.plate}</Tag>}
                            {!v.active && <Tag>{t("Archived")}</Tag>}
                          </Space>
                        }
                        description={
                          <Space wrap size={4}>
                            <span>
                              {t("{{count}} seats", { count: v.seats })}
                              {v.licenseClass
                                ? ` · ${t("class {{c}}", { c: v.licenseClass })}`
                                : ""}
                            </span>
                            {inspectionTag(v, t)}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </>
            )
          },
          {
            key: "team",
            label: t("Team"),
            children: (
              <List
                dataSource={meta?.staff || []}
                renderItem={person => (
                  <List.Item>
                    <div className="dsp-full">
                      <Space className="dsp-between">
                        <Typography.Text strong>{person.name}</Typography.Text>
                        <Checkbox
                          checked={!person.hidden}
                          disabled={!canEdit}
                          onChange={e => saveStaff(person, { hidden: !e.target.checked })}
                        >
                          {t("On the board")}
                        </Checkbox>
                      </Space>
                      {person.qualificationAlerts?.length > 0 && (
                        <Space wrap size={4} className="dsp-quals">
                          {person.qualificationAlerts.map(q => (
                            <Tag
                              key={`${q.type}-${q.title}`}
                              color={q.state === "overdue" ? "error" : "warning"}
                            >
                              {q.title || qualificationLabels(t)[q.type] || q.type} ·{" "}
                              {formatDay(q.validUntil)}
                            </Tag>
                          ))}
                        </Space>
                      )}
                      <Select
                        mode="tags"
                        className="dsp-full"
                        placeholder={t("Skills, e.g. Gas, Elektro, Dach")}
                        value={person.skills}
                        disabled={!canEdit}
                        options={(meta?.skills || []).map(s => ({ value: s, label: s }))}
                        onChange={skills => saveStaff(person, { skills })}
                      />
                      <Select
                        allowClear
                        className="dsp-full dsp-section"
                        placeholder={t("Usual vehicle")}
                        value={person.defaultVehicle || undefined}
                        disabled={!canEdit}
                        options={vehicles
                          .filter(v => v.active)
                          .map(v => ({ value: v.id, label: v.name }))}
                        onChange={value => saveStaff(person, { defaultVehicle: value || null })}
                      />
                    </div>
                  </List.Item>
                )}
              />
            )
          }
        ]}
      />
      <VehicleForm
        open={editing !== undefined}
        vehicle={editing}
        licenseClasses={meta?.licenseClasses}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined);
          onChanged();
        }}
      />
    </Drawer>
  );
};

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/** Working days and hours, Betriebshof postcode, travel factors (owner). */
export const SettingsModal = ({ open, settings, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) form.setFieldsValue({ ...settings, homeZip: settings?.storedHomeZip || "" });
  }, [open]);
  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await saveSettings(values);
      Success(t("Saved"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };
  // 2026-10-05 is a Monday: weekday names in the user's language
  const weekdayName = d => dayLabel(addDays("2026-10-04", d), i18n.language).split(/[,\s]+/)[0];
  const clock = { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: t("Format HH:mm") };
  return (
    <Modal
      rootClassName="dispatch-page"
      open={open}
      onCancel={onClose}
      title={t("Dispatch settings")}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>
          {t("Save")}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="workdays"
          label={t("Working days")}
          extra={t("Employees with their own working days or shifts are checked against those.")}
        >
          <Checkbox.Group options={WEEKDAYS.map(d => ({ value: d, label: weekdayName(d) }))} />
        </Form.Item>
        <Row gutter={[16, 0]}>
          <Col xs={12}>
            <Form.Item name="dayStart" label={t("Working day from")} rules={[clock]}>
              <Input placeholder="07:00" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="dayEnd" label={t("until")} rules={[clock]}>
              <Input placeholder="16:00" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="homeZip"
          label={t("Postcode of the yard (start of the first trip)")}
          extra={t("Empty: the company's postcode.")}
          rules={[{ pattern: /^(\d{5})?$/, message: t("Five digits") }]}
        >
          <Input maxLength={5} inputMode="numeric" />
        </Form.Item>
        <Row gutter={[16, 0]}>
          <Col xs={12}>
            <Form.Item name="travelFactor" label={t("Road factor")}>
              <InputNumber min={1} max={3} step={0.1} className="dsp-full" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="speedKmh" label={t("Average speed (km/h)")}>
              <InputNumber min={10} max={130} precision={0} className="dsp-full" />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Paragraph type="secondary" className="dsp-small">
          {t("Travel times are rough estimates from postcode areas (±30 km), not a route planner.")}
        </Typography.Paragraph>
      </Form>
    </Modal>
  );
};

/** Week plan as PDF (one employee or the team) and by email to each employee. */
export const PlanModal = ({ open, from, staff, canEmail, onClose }) => {
  const { t } = useTranslation("dashboard");
  const [employee, setEmployee] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open) setSelected((staff || []).filter(s => s.email && !s.hidden).map(s => s.id));
  }, [open]);
  const send = async () => {
    setSending(true);
    try {
      const res = await emailPlans({ from, employees: selected });
      const { sent, skipped, failed } = res.data.data;
      Modal.info({
        rootClassName: "dispatch-page",
        title: t("Week plans sent"),
        content: (
          <>
            <p>{t("Sent: {{count}}", { count: sent.length })}</p>
            {skipped.length > 0 && (
              <p>
                {t("No email address: {{names}}", { names: skipped.map(s => s.name).join(", ") })}
              </p>
            )}
            {failed.length > 0 && (
              <p>{t("Failed: {{names}}", { names: failed.map(s => s.name).join(", ") })}</p>
            )}
          </>
        )
      });
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSending(false);
    }
  };
  return (
    <Modal
      rootClassName="dispatch-page"
      open={open}
      onCancel={onClose}
      title={t("Week plan")}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("Close")}
        </Button>
      ]}
    >
      <Space direction="vertical" className="dsp-full">
        <Typography.Text strong>{t("PDF")}</Typography.Text>
        <Select
          allowClear
          className="dsp-full"
          placeholder={t("Whole team")}
          value={employee || undefined}
          onChange={value => setEmployee(value || null)}
          options={(staff || []).map(s => ({ value: s.id, label: s.name }))}
        />
        <Button block href={planPdfUrl({ from, employee })} target="_blank" rel="noreferrer">
          {t("Open PDF")}
        </Button>
        {canEmail && (
          <>
            <Typography.Text strong className="dsp-section">
              {t("Email each employee their plan")}
            </Typography.Text>
            <Select
              mode="multiple"
              className="dsp-full"
              value={selected}
              onChange={setSelected}
              options={(staff || []).map(s => ({
                value: s.id,
                label: s.email ? s.name : `${s.name} (${t("no email")})`,
                disabled: !s.email
              }))}
            />
            <Button
              block
              type="primary"
              icon={<MailOutlined />}
              loading={sending}
              disabled={!selected.length}
              onClick={send}
            >
              {t("Send week plans")}
            </Button>
          </>
        )}
      </Space>
    </Modal>
  );
};
